import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/issue-create/hooks/use-should-show-copilot-functionality-in-issues'
import type {ReactNode} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {IssueViewerIssue$data} from '../__generated__/IssueViewerIssue.graphql'
import type {
  IssueTimelineIssueFragment$data,
  IssueTimelineIssueFragment$key,
} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import type {IssueTimelineSecondary$key} from './__generated__/IssueTimelineSecondary.graphql'
import type {EventProps} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {TabbedTimeline} from './TabbedTimeline'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItem$data>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
    tabbedTimeline: {type: "Boolean", defaultValue: false}
    viewType: {type: "TimelineEventViewType"}
  ) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip) @skip(if: $tabbedTimeline)
    ...useTimelineItemsBackFragment @skip(if: $tabbedTimeline)
    ...TabbedTimelineFragment @arguments(viewType: $viewType) @include(if: $tabbedTimeline)
  }
`

type IssueTimelineProps = {
  issue: IssueViewerIssue$data
  issueSecondary: IssueTimelineSecondary$key | undefined
  highlightedEvent: string | undefined
  page?: number
} & EventProps
export const IssueTimeline = ({
  issue,
  issueSecondary,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: IssueTimelineProps) => {
  const data = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)
  const secondaryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueTimelineSecondary on Issue {
        isTransferInProgress
      }
    `,
    issueSecondary,
  )

  const showCopilotMenuItem = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: data.repository.visibility,
    userCanPushToRepo: data.repository.viewerCanPush,
  })

  return (
    <>
      {optionConfig.tabbedTimeline ? (
        <TabbedTimeline
          data={data}
          highlightedEvent={highlightedEvent}
          page={page}
          showCopilotMenuItem={showCopilotMenuItem}
          isTransferInProgress={secondaryData?.isTransferInProgress}
          viewer={viewer}
          onCommentChange={onCommentChange}
          onCommentReply={onCommentReply}
          onCommentEditCancel={onCommentEditCancel}
          optionConfig={optionConfig}
        />
      ) : (
        <ClassicTimeline
          data={data}
          highlightedEvent={highlightedEvent}
          page={page}
          showCopilotMenuItem={showCopilotMenuItem}
          isTransferInProgress={secondaryData?.isTransferInProgress}
          viewer={viewer}
          onCommentChange={onCommentChange}
          onCommentReply={onCommentReply}
          onCommentEditCancel={onCommentEditCancel}
          optionConfig={optionConfig}
        />
      )}
    </>
  )
}

/**
 * Classic (non-tabbed) timeline. Separated into its own component so that the
 * `useTimelineItems` hook (which depends on the classic fragments) is only
 * mounted when those fragments are present in the response.
 */
type ClassicTimelineProps = {
  data: IssueTimelineIssueFragment$data
  highlightedEvent: string | undefined
  page?: number
  showCopilotMenuItem?: boolean
  isTransferInProgress?: boolean
} & EventProps
const ClassicTimeline = ({
  data,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  showCopilotMenuItem,
  isTransferInProgress,
}: ClassicTimelineProps) => {
  const timelinePaginationForBotsEnabled = isFeatureEnabled('issues_react_bots_timeline_pagination')
  const effectivePage = timelinePaginationForBotsEnabled ? page : undefined

  const {timelineItems, totalItemCount, highlightedReady} = useTimelineItems({
    timelineData: data,
    highlightedEvent,
    page: effectivePage,
  })

  return (
    <TimelinePanel
      timelineItems={timelineItems}
      totalItemCount={totalItemCount}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={effectivePage}
      isTransferInProgress={isTransferInProgress}
      showCopilotMenuItem={showCopilotMenuItem}
      viewer={viewer}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
    />
  )
}
